




















































































import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ButtonType } from '@getaccept/lib-ui';
import type { Channel } from '@getaccept/pusher';
import { RecipientEvent } from '@getaccept/pusher';
import type { PropType } from '@vue/composition-api';
import { computed, defineComponent, onMounted, ref, watch } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import { useEntityStore } from '../../../entity/store/entity.store';
import { userLanguage } from '../../../lang';
import { pusherClient } from '../../../pusher';
import { useSigningStore } from '../../../signing/store/signing.store';
import { getIdOptions } from '../../constants/eid-options';
import type { Id } from '../../types/eid';
import { BankIdSigningType, BankIdStep } from '../../types/enums/bankid-se';
import { CriiptoStatus } from '../../types/enums/criipto-status';
import { EidType } from '../../types/enums/eid-type';
import BankIdOption from '../BankIdOption.vue';
import BankIdSe from './BankIdSe.vue';
import CriiptoButton from './CriiptoButton.vue';

export default defineComponent({
  components: {
    BankIdSe,
    BankIdOption,
    CriiptoButton,
  },
  props: {
    documentId: {
      type: String,
      required: true,
    },
    recipientId: {
      type: String,
      default: '',
    },
    authId: {
      type: String,
      default: '',
    },
    jwt: {
      type: String,
      required: true,
    },
    reviewPending: {
      type: Boolean,
    },
    recipientPusherChannel: {
      type: Object as PropType<Channel>,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const openBankIdPicker = ref(false);
    const options = ref<Id[]>([]);

    const signingStore = useSigningStore();

    const { bankId, criiptoStatus, selectedEid } = storeToRefs(signingStore);
    const entityStore = useEntityStore();

    const actionCancelBankIdTransaction = (ssn?: string) => {
      signingStore.cancelBankIdTransaction(entityStore.entity.id, ssn);
    };

    const pollBankIdTransaction = (payload: {
      orderRef: string;
      documentId: string;
      recipientId: string;
    }) => {
      signingStore.pollBankIdTransaction(payload.orderRef, payload.documentId, payload.recipientId);
    };

    const setBankIdState = (payload: { step: BankIdStep; signingType: BankIdSigningType }) => {
      signingStore.setBankIdState(payload.step, payload.signingType);
    };

    const setCriiptoStatus = (status: CriiptoStatus) => {
      signingStore.setCriiptoStatus(status);
    };

    const setSelectedEid = (eid: Id) => {
      signingStore.setSelectedEid(eid);
    };

    const criiptoRedirect = () => {
      signingStore.redirectCriipto();
    };

    const handleWatchCriiptoStatus = (status: CriiptoStatus) => {
      if (status === CriiptoStatus.Success) {
        handleSignDocument();
      }
    };

    watch(criiptoStatus, handleWatchCriiptoStatus);

    const placeholder = computed(() => selectedEid.value?.name || t('select-an-electronic-id'));

    const showBankIDSe = computed(
      () => !!selectedEid.value && selectedEid.value.key === EidType.SE
    );

    const showCriiptoButton = computed(() =>
      [EidType.MitID, EidType.BankIDFI, EidType.NO].includes(selectedEid.value?.key)
    );

    const showEidPicker = computed(() => criiptoStatus.value !== CriiptoStatus.Loading);

    const bankIdStarted = computed(() => bankId.value.signingType !== BankIdSigningType.None);

    const selected = (option: Id) => selectedEid.value?.key === option.key;

    const defaultBankIdOption = (userLang: string): Id => {
      switch (userLang) {
        case 'sv':
          return options.value.find(option => option.key === EidType.SE);
        case 'da':
          return options.value.find(option => option.key === EidType.MitID);
        case 'no':
        case 'nn':
        case 'nb':
          return options.value.find(option => option.key === EidType.NO);
        case 'fi':
          return options.value.find(option => option.key === EidType.BankIDFI);
        default:
          return null;
      }
    };

    const handleRecipientsInput = (selectedEid: Id) => {
      handleCancelBankId();
      setSelectedEid(selectedEid);
      openBankIdPicker.value = false;
    };

    const handleFirstRecipientsInput = (selectedEid: Id) => {
      setSelectedEid(selectedEid);
      addCriiptoSubscription();
      openBankIdPicker.value = false;
    };

    const handleCriiptoRedirect = () => {
      criiptoRedirect();
    };

    const handleCancelBankId = () => {
      actionCancelBankIdTransaction();
    };

    const handleSignDocument = () => {
      if (props.reviewPending) {
        handleClose();
        return;
      }
      emit('sign');
      actionCancelBankIdTransaction();
    };

    const handleToggleBankIdPicker = (open: boolean) => {
      openBankIdPicker.value = open;
    };

    const handleClose = () => {
      handleCancelBankId();
      emit('close');
    };

    const addCriiptoSubscription = () => {
      pusherClient.listen(
        props.recipientPusherChannel,
        RecipientEvent.CriiptoStatusUpdate,
        ({ criiptoStatus }: { criiptoStatus: CriiptoStatus }) => {
          setCriiptoStatus(criiptoStatus);
        }
      );
    };

    const setDefaultEid = () => {
      if (!selectedEid.value) {
        setSelectedEid(defaultBankIdOption(userLanguage()));
      }
    };

    onMounted(() => {
      options.value = getIdOptions();
      setDefaultEid();
      if (selectedEid.value) {
        addCriiptoSubscription();
      }
      if (criiptoStatus.value === CriiptoStatus.Success) {
        handleSignDocument();
      }
    });

    const created = () => {
      if (bankId.value.orderRef) {
        pollBankIdTransaction({
          orderRef: bankId.value.orderRef,
          documentId: props.documentId,
          recipientId: props.recipientId,
        });
      }
    };

    created();

    return {
      ButtonType,
      BankIdStep,
      t,
      showCriiptoButton,
      showBankIDSe,
      showEidPicker,
      bankIdStarted,
      selected,
      placeholder,
      options,
      criiptoStatus,
      selectedEid,
      openBankIdPicker,
      handleRecipientsInput,
      handleFirstRecipientsInput,
      handleCriiptoRedirect,
      handleCancelBankId,
      handleSignDocument,
      handleToggleBankIdPicker,
      handleClose,
      bankId,
      handleWatchCriiptoStatus,
      actionCancelBankIdTransaction,
      pollBankIdTransaction,
      setBankIdState,
      setCriiptoStatus,
      setSelectedEid,
      criiptoRedirect,
      defaultBankIdOption,
    };
  },
});
